import { IndicatorFactType, PlotReportFactElement, PlotReportFactLabelFilter } from './fact';
// eslint-disable-next-line import/no-cycle
import { PlotType } from './plot';
// TODO: (MVP-1132): these types should be generated automatically based on the API's Swagger documentation
import {
  FILTER_OPERATORS,
  PlotGroupFiltersEnum,
  PlotGroupSortEnum,
  PlotLabelName,
  PlotLabelValue,
  SortOptions,
} from './plotGroup';
import { REPORT_TYPES } from './reports';

export const GLOBAL_DATA_ORGANIZATION_NAME = 'Global Data';

export enum OrganizationTypeEnum {
  DEMO = 'DEMO',
  MANAGED = 'MANAGED',
  USER = 'USER',
}

export type Currency = {
  code: string;
  name: string;
};

export type Organization = {
  id: string;
  name: string;
  type: OrganizationTypeEnum;
  plots_count: number;
  currency_code?: string;
  conversion_rate?: number;
  modified_at?: string;
};

export type StewardedValue = {
  value: number;
  currency: string;
  qtd_change: number; // TODO: MVP-CW decide whether to remove this if the BE still does not provide this value. "qtd_change" is a random name according to the figma designs
};

type FilterField = {
  field: keyof typeof PlotGroupFiltersEnum;
  operator: FILTER_OPERATORS;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
};

type GroupByField = {
  label_name_id?: string | null;
};

export type FilterDefinition = {
  group_by?: GroupByField[];
  field_filters: FilterField[];
  sort_orders: {
    field: keyof typeof PlotGroupSortEnum;
    direction: SortOptions;
  }[];
};

export type PlotGroupBodyData = {
  name: string;
  filter_definition: FilterDefinition;
  additional_plot_ids: string[];
};

export type PlotGroupSuccessResponse = {
  id: string;
  name: string;
  organization: string;
  filter_definition: FilterDefinition;
  additional_plot_ids: string[];
  total_plots?: number;
  total_area?: number;
};

export type PlotGroupComparison = {
  id?: string;
  name?: string;
  organization?: string;
  plot_groups?: PlotGroupSuccessResponse[];
  indicators?: IndicatorFactType[];
  modified_at?: string;
};

export type PlotGroupEIIStats = {
  [plotGroupId: string]: {
    ecological_integrity_score: number | null;
    functional_integrity_score: number | null;
    compositional_integrity_score: number | null;
    structural_integrity_score: number | null;
    total_plots: number;
    total_area: number;
  };
};

export enum RoleDescription {
  landsteward = 'landsteward',
  manager = 'manager',
  investor = 'investor',
}

export enum Industry {
  agriculture = 'agriculture',
  automotive = 'automotive',
  finance = 'finance',
  real_estate = 'real_estate',
  mining = 'mining',
  retail = 'retail',
  other = 'other',
}

export enum GoalPaths {
  investor_perception = 'investor_perception',
  esg_indices = 'esg_indices',
  capital_opportunity = 'capital_opportunity',
  operations = 'operations',
  compliance = 'compliance',
  insurability = 'insurability',
  public_funds = 'public_funds',
  financial_risk = 'financial_risk',
  invest = 'invest',
}

export enum Goal {
  invest = 'invest',
  financial_risk = 'financial_risk',
  capital_opportunity = 'capital_opportunity',
}

export const GoalPathsMap = {
  [Goal.invest]: [GoalPaths.public_funds, GoalPaths.financial_risk, GoalPaths.invest],
  [Goal.financial_risk]: [GoalPaths.operations, GoalPaths.compliance, GoalPaths.insurability],
  [Goal.capital_opportunity]: [GoalPaths.investor_perception, GoalPaths.esg_indices, GoalPaths.capital_opportunity],
};

export enum ComplianceFrameworks {
  csrd = 'csrd',
  eudr = 'eudr',
  greenhouse = 'greenhouse',
  npi = 'npi',
  rac = 'rac',
  sai = 'sai',
  sbti = 'sbti',
  sbtn = 'sbtn',
  science = 'science',
  tnfd = 'tnfd',
  gri = 'gri',
  cdp = 'cdp',
  sdg = 'sdg',
  iucn = 'iucn',
  cbd = 'cbd',
  gbf = 'gbf',
}

export type CoPilotConfig = {
  profile: RoleDescription | null;
  industry: Industry | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  goals: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  goals_compliance: any;
  goals_context: string | null;
};

export type OrganizationReport = {
  id: string;
  report_name: string;
  report_date: string;
  created_at: string;
  file_download_url: string;
  thumbnail_url?: string | null;
  report_type: (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];
  plot_group_id: string | null;
  is_filtered: boolean;
};

export type NCAContributionsAndEarnings = {
  name: PlotType;
  unit: string;
  values: {
    total_contributions_reference_date: Date;
    total_contributions: number;
    total_earnings_reference_date: Date;
    total_earnings_value: number;
  };
};

export type LandTypePctDistribution = {
  name: PlotType;
  unit: string;
  value: number;
};

export enum NaturalCapitalDimension {
  WATER = 'water',
  CARBON = 'carbon',
  SOIL = 'soil',
  BIODIVERSITY = 'biodiversity',
}

export enum UpliftUnitDimension {
  water_default = 'water_default',
  water_pedotransfer_legacy = 'water_pedotransfer_legacy',
  water_lobelia = 'water_lobelia',
  co2_v3 = 'co2_v3',
  co2_default = 'co2_default',
  co2_legacy = 'co2_legacy',
  soil_default = 'soil_default',
  biodiversity_default = 'biodiversity_default',
  vnu = 'vnu',
}

export type LandTypeNCA = {
  land_type: PlotType;
  data: {
    dimension: NaturalCapitalDimension;
    opening_stock: number;
    opening_stock_reference_date: string;
    closing_stock: number;
    closing_stock_reference_date: string;
    yoy_change: number;
    yoy_change_previous_1y_reference_date: string;
    yoy_change_previous_1y: number;
    unit: string;
    possibility?: number;
  }[];
  unit: string;
  value: number;
};

export type Claim = {
  uplift_unit_id: string;
  dimension: keyof typeof UpliftUnitDimension;
  project_name: string;
  plot_name: string;
  landsteward_name: string;
  investor_name: string;
  issued_at: string;
  expires_at: string;
  rdi: number;
  issuance_level: number;
  owned_by: string;
  fact_name: IndicatorFactType;
  active: boolean;
  plot_id?: string;
};

export type LandTypeNCARow = LandTypeNCA['data'][0];

export enum RoadmapNodeStatus {
  created = 'created',
  ready = 'ready',
  active = 'active',
  error = 'error',
  closed = 'closed',
}

export type RoadmapNode = {
  id: string | null;
  organization: string;
  parent: string;
  title: string;
  status: RoadmapNodeStatus;
  description?: string;
  visible_to_customer: boolean;
  children?: RoadmapNode[];
};

export enum OrganizationUIStyle {
  CONSERVATION = 'conservation',
  DEFAULT = 'default',
}

export type OrganizationProduct = {
  id: string;
  name: string;
  price: number | null;
  ui_style: OrganizationUIStyle;
};

/**
 * Organization contract related types
 */
export type OrganizationContract = {
  id: string;
  name: string;
  seller_org: string;
  seller_org_name: string;
  buyer_org: string;
  buyer_org_name: string;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  file_name: string;
};

export type OrganizationContractDownload = {
  id: string;
  file_url: string;
  file_name: string;
};

export type OrganizationReportingFacts = {
  facts: PlotReportFactElement[];
};

export type OrganizationSummary = {
  organization_id: string;
  summary?: string | null;
  modified_by: string;
  modified_at: string;
  created_at: string;
  created_by: string;
};

export enum GeomodelServerGroupBy {
  labels = 'labels',
  date = 'date',
  week = 'week',
  year = 'year',
  month = 'month',
}

export enum GeomodelServerLabelFiltersOperation {
  exact_match = 'exact_match',
  contains = 'contains',
}

/**
 * GET /api/v1/organization/
 */
export const GET_ORGANIZATION_PATH = 'organization';
export type GetOrganizationsRequestPathVariables = undefined;
export type GetOrganizationsRequestQueryParams =
  | {
      query?: string;
      sort?: string;
      limit?: number;
      offset?: number;
      id?: string;
    }
  | undefined;
export type GetOrganizationsSuccessDto = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Organization[];
};
export type GetOrganizationsErrorDto = Error;

/**
 * PATCH /api/v1/organization/{id}/
 */
export const PATCH_ORGANIZATION_PATH = 'organization/{id}/';
export type PatchOrganizationRequestPathVariables = { id: string };
export type PatchOrganizationRequestQueryParams = undefined;
export type PatchOrganizationRequestBodyData = {
  currency_code?: string;
};
export type PatchOrganizationSuccessDto = Organization;
export type PatchOrganizationErrorDto = Error;

/**
 * GET /api/v1/dashboard/organization-summary/{id}
 */
export const GET_DASHBOARD_ORGANIZATION_SUMMARY_PATH = 'dashboard/organization-summary/{id}';
export type GetDashboardOrganizationSummaryRequestPathVariables = { id: string };
export type GetDashboardOrganizationSummaryRequestQueryParams = { plot_group_id?: string; workstream_id?: string };
export type GetDashboardOrganizationSummarySuccessDto = {
  id: string;
  total_stewarded_value_total: StewardedValue;
  total_stewarded_value_per_ha: StewardedValue;
  total_size: number; // in m^3
  total_assets: number; // number of plots
  average_ecological_integrity_score: number;
};
export type GetDashboardOrganizationSummaryErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/land-type/
 */
export const GET_ORGANIZATION_LAND_TYPE_PATH = 'organization/{organization_id}/land-type/';
export type GetOrganizationLandTypeRequestPathVariables = {
  organization_id: string;
};
export type GetOrganizationLandTypeRequestQueryParams = {
  plot_group_id?: string;
  workstream_id?: string;
};
export type GetOrganizationLandTypeSuccessDto = LandTypePctDistribution[];
export type GetOrganizationLandTypeErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/natural-capital-account/
 */
export const GET_ORGANIZATION_NATURAL_CAPITAL_ACCOUNT_PATH = 'organization/{organization_id}/natural-capital-account/';
export type GetOrganizationNaturalCapitalAccountRequestPathVariables = {
  organization_id: string;
};
export type GetOrganizationNaturalCapitalAccountRequestQueryParams = {
  plot_group_id?: string;
  workstream_id?: string;
};
export type GetOrganizationNaturalCapitalAccountSuccessDto = {
  land_type_percent_distribution: LandTypePctDistribution[];
  land_type_contributions_and_earnings: NCAContributionsAndEarnings[];
  land_type_nca: LandTypeNCA[];
};
export type GetOrganizationNaturalCapitalAccountErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/plot-groups
 */
export const GET_ORGANIZATION_PLOT_GROUPS_PATH = 'organization/{id}/plot-groups';
export type GetOrganizationPlotGroupsRequestPathVariables = { id: string };
export type GetOrganizationPlotGroupsRequestQueryParams = {
  name_exclude?: string;
  workstream_id?: string;
};
export type GetOrganizationPlotGroupsSuccessDto = PlotGroupSuccessResponse[];
export type GetOrganizationPlotGroupsErrorDto = Error;

/**
 * POST /api/v1/organization/{organization_id}/plot-groups/create
 */
export const POST_ORGANIZATION_PLOT_GROUPS_PATH = 'organization/{id}/plot-groups/create';
export type PostOrganizationPlotGroupsRequestPathVariables = { id: string };
export type PostOrganizationPlotGroupsRequestQueryParams = { workstream_id?: string } | undefined;
export type PostOrganizationPlotGroupsRequestBodyData = PlotGroupBodyData;
export type PostOrganizationPlotGroupsSuccessDto = PlotGroupSuccessResponse;
export type PostOrganizationPlotGroupsErrorDto = Error;

/**
 * PATCH /api/v1/organization/{org_id}/plot-groups/{id}/update
 */
export const PATCH_ORGANIZATION_PLOT_GROUPS_PATH = 'organization/{org_id}/plot-groups/{id}/update';
export type PatchOrganizationPlotGroupsRequestPathVariables = { org_id: string; id: string };
export type PatchOrganizationPlotGroupsRequestQueryParams = undefined;
export type PatchOrganizationPlotGroupsRequestBody = PlotGroupBodyData;
export type PatchOrganizationPlotGroupsSuccessDto = PlotGroupSuccessResponse;
export type PatchOrganizationPlotGroupsErrorDto = Error;

/**
 * DELETE /api/v1/organization/{organization_id}/plot-groups/{plot_group_id}/delete
 */
export const DELETE_ORGANIZATION_PLOT_GROUPS_PATH = 'organization/{organization_id}/plot-groups/{plot_group_id}/delete';
export type DeleteOrganizationPlotGroupsRequestPathVariables = { organization_id: string; plot_group_id: string };
export type DeleteOrganizationPlotGroupsRequestQueryParams = undefined;
export type DeleteOrganizationPlotGroupsRequestBody = undefined;
export type DeleteOrganizationPlotGroupsSuccessDto = undefined;
export type DeleteOrganizationPlotGroupsErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/plot-groups/eii-stats
 */
export const GET_ORGANIZATION_PLOT_GROUPS_EII_STATS_PATH = 'organization/{organization_id}/plot-groups/eii-stats';
export type GetOrganizationPlotGroupEIIStatsRequestPathVariables = { organization_id: string };
export type GetOrganizationPlotGroupEIIStatsRequestQueryParams = undefined;
export type GetOrganizationPlotGroupEIIStatsSuccessDto = PlotGroupEIIStats;
export type GetOrganizationPlotGroupEIIStatsErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/plot-group-comparisons
 */
export const GET_ORGANIZATION_PLOT_GROUP_COMPARISONS_PATH = 'organization/{organization_id}/plot-group-comparisons';
export type GetOrganizationPlotGroupComparisonsRequestPathVariables = {
  organization_id: string;
  workstream_id?: string;
};
export type GetOrganizationPlotGroupComparisonsRequestQueryParams = undefined;
export type GetOrganizationPlotGroupComparisonsSuccessDto = PlotGroupComparison[];
export type GetOrganizationPlotGroupComparisonsErrorDto = Error;
/**
 * POST /api/v1/organization/{organization_id}/plot-group-comparisons/create
 */
export const POST_ORGANIZATION_PLOT_GROUP_COMPARISONS_PATH =
  'organization/{organization_id}/plot-group-comparisons/create';
export type PostOrganizationPlotGroupComparisonsRequestPathVariables = { organization_id: string };
export type PostOrganizationPlotGroupComparisonsRequestQueryParams = undefined;
export type PostOrganizationPlotGroupComparisonsRequestBodyData = {
  id: string | null;
  name: string;
  plot_group_ids: string[];
  indicators?: IndicatorFactType[];
};
export type PostOrganizationPlotGroupComparisonsSuccessDto = PlotGroupComparison;
export type PostOrganizationPlotGroupComparisonsErrorDto = Error;

/**
 * DELETE /api/v1/organization/{organization_id}/plot-group-comparisons/{plot_group_comparison_id}/delete
 */
export const DELETE_ORGANIZATION_PLOT_GROUP_COMPARISONS_PATH =
  'organization/{organization_id}/plot-group-comparisons/{plot_group_comparison_id}/delete';
export type DeleteOrganizationPlotGroupComparisonsRequestPathVariables = {
  organization_id: string;
  plot_group_comparison_id: string;
};
export type DeleteOrganizationPlotGroupComparisonsRequestQueryParams = undefined;
export type DeleteOrganizationPlotGroupComparisonsRequestBody = undefined;
export type DeleteOrganizationPlotGroupComparisonsSuccessDto = undefined;
export type DeleteOrganizationPlotGroupComparisonsErrorDto = Error;

/**
 * GET /api/v1/plot-label-names/
 */
export const GET_PLOT_LABEL_NAMES_PATH = 'plot-label-names';
export type GetPlotLabelNamesRequestPathVariables = undefined;
export type GetPlotLabelNamesRequestQueryParams = {
  limit?: number;
  offset?: number;
  organization_id: string;
};
export type GetPlotLabelNamesSuccessDto = PlotLabelName[];
export type GetPlotLabelNamesErrorDto = Error;

/**
 * GET /api/v1/plot-label-names/{plot_label_name_id}/values
 */
export const GET_PLOT_LABEL_VALUES_PATH = 'plot-label-names/{plot_label_name_id}/values';
export type GetPlotLabelValuesRequestPathVariables = { plot_label_name_id: string };
export type GetPlotLabelValuesRequestQueryParams = {
  limit?: number;
  offset?: number;
};
export type GetPlotLabelValuesSuccessDto = {
  count: number;
  next: null | string;
  previous: null | string;
  results: PlotLabelValue[];
};
export type GetPlotLabelValuesErrorDto = Error;

/**
 * GET /api/v1/plot-label-names/values/
 */
export const GET_ALL_PLOT_LABEL_VALUES_PATH = 'plot-label-names/values';
export type GetAllPlotLabelValuesRequestPathVariables = undefined;
export type GetAllPlotLabelValuesRequestQueryParams = {
  limit?: number;
  offset?: number;
  organization_id: string;
};
export type GetAllPlotLabelValuesSuccessDto = {
  count: number;
  next: null | string;
  previous: null | string;
  results: PlotLabelValue[];
};
export type GetAllPlotLabelValuesErrorDto = Error;

/**
 * POST /api/v1/plot-label-names/
 */
export const POST_PLOT_LABEL_NAME_PATH = 'plot-label-names';
export type PostPlotLabelNameRequestPathVariables = undefined;
export type PostPlotLabelNameRequestQueryParams = undefined;
export type PostPlotLabelNameRequestBodyData = {
  name: string;
  organization: string;
};
export type PostPlotLabelNameSuccessDto = PlotLabelName;
export type PostPlotLabelNameErrorDto = Error;

/**
 * POST /api/v1/plot-label-names/{plot_label_name_id}/values
 */
export const POST_PLOT_LABEL_VALUE_PATH = 'plot-label-names/{plot_label_name_id}/values';
export type PostPlotLabelValueRequestPathVariables = { plot_label_name_id: string };
export type PostPlotLabelValueRequestQueryParams = undefined;
export type PostPlotLabelValueRequestBodyData = { value: string };
export type PostPlotLabelValueSuccessDto = PlotLabelValue;
export type PostPlotLabelValueErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/reporting-facts
 */
export const GET_ORGANIZATION_REPORTING_FACTS_PATH = 'organization/{organization_id}/reporting-facts';
export type GetOrganizationReportingFactsRequestPathVariables = { organization_id: string };
export type GetOrganizationReportingFactsRequestQueryParams =
  | (PlotReportFactLabelFilter & {
      plot_group_id?: string;
      fact_names?: string;
      workstream_id?: string;
      source_ref?: string;
      group_by?: string; // join.(GeomodelServerGroupBy[]);
      label_filters_operation?: GeomodelServerLabelFiltersOperation;
      limit?: number;
    })
  | undefined;
export type GetOrganizationReportingFactsSuccessDto = OrganizationReportingFacts;
export type GetOrganizationReportingFactsErrorDto = Error;
/**
 * GET /api/v1/organization/{organization_id}/graph-facts
 */
export const GET_ORGANIZATION_GRAPH_FACTS_PATH = 'organization/{organization_id}/graph-facts';
export type GetOrganizationGraphFactsRequestPathVariables = { organization_id: string };
export type GetOrganizationGraphFactsRequestQueryParams =
  | (PlotReportFactLabelFilter & {
      plot_group_id?: string;
      fact_names?: string;
      workstream_id?: string;
      source_ref?: string;
      group_by?: string; // join.(GeomodelServerGroupBy[]);
      label_filters_operation?: GeomodelServerLabelFiltersOperation;
      limit?: number;
    })
  | undefined;
export type GetOrganizationGraphFactsSuccessDto = OrganizationReportingFacts;
export type GetOrganizationGraphFactsErrorDto = Error;

/**
 * GET /api/v1/dashboard/organization/co-pilot-config/{organization_id}
 */
export const GET_CO_PILOT_CONFIG_PATH = 'dashboard/co-pilot-config/{organization_id}';
export type GetCoPilotConfigRequestPathVariables = { organization_id: string };
export type GetCoPilotConfigRequestQueryParams = undefined;
export type GetCoPilotConfigSuccessDto = CoPilotConfig;
export type GetCoPilotConfigErrorDto = Error;

/**
 * POST /api/v1/dashboard/organization/co-pilot-config/{organization_id}
 */
export const POST_CO_PILOT_CONFIG_PATH = 'dashboard/co-pilot-config/{organization_id}';
export type PostCoPilotConfigRequestPathVariables = { organization_id: string };
export type PostCoPilotConfigRequestQueryParams = undefined;
export type PostCoPilotConfigRequestBodyData = CoPilotConfig;
export type PostCoPilotConfigSuccessDto = CoPilotConfig;
export type PostCoPilotConfigErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/reports/
 */
export const GET_ORGANIZATION_REPORTS_PATH = 'organization/{organization_id}/reports';
export type GetOrganizationReportsRequestPathVariables = { organization_id: string };
export type GetOrganizationReportsRequestQueryParams = undefined;
export type GetOrganizationReportsSuccessDto = {
  results: OrganizationReport[];
  count: number;
};
export type GetOrganizationReportsErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/contracts/
 */
export const GET_ORGANIZATION_CONTRACTS_PATH = 'organization/{organization_id}/contracts';
export type GetOrganizationContractsRequestPathVariables = { organization_id: string };
export type GetOrganizationContractsRequestQueryParams = undefined;
export type GetOrganizationContractsSuccessDto = {
  results: OrganizationContract[];
  count: number;
};
export type GetOrganizationContractsErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/contracts/{contract_id}/download/
 */
export const GET_ORGANIZATION_CONTRACT_DOWNLOAD_PATH =
  'organization/{organization_id}/contracts/{contract_id}/download';
export type GetOrganizationContractDownloadRequestPathVariables = {
  organization_id: string;
  contract_id: string;
};
export type GetOrganizationContractDownloadRequestQueryParams = undefined;
export type GetOrganizationContractDownloadSuccessDto = OrganizationContractDownload;
export type GetOrganizationContractDownloadErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/claims/
 */
export const GET_ORGANIZATION_CLAIMS_PATH = 'organization/{organization_id}/claims';
export type GetOrganizationClaimsRequestPathVariables = { organization_id: string };
export type GetOrganizationClaimsRequestQueryParams =
  | {
      limit?: number;
      offset?: number;
      plot_group_id?: string | null;
    }
  | undefined;
export type GetOrganizationClaimsSuccessDto = {
  results: Claim[];
  count: number;
  next: string | null;
  previous: string | null;
};
export type GetOrganizationClaimsErrorDto = Error;

/**
 * GET /api/v1/organization/available_currencies/
 */
export const GET_ORGANIZATION_AVAILABLE_CURRENCIES_PATH = 'organization/available_currencies/';
export type GetAvailableCurrenciesRequestPathVariables = undefined;
export type GetAvailableCurrenciesRequestQueryParams = undefined;
export type GetAvailableCurrenciesSuccessDto = {
  currencies: Currency[];
};
export type GetAvailableCurrenciesErrorDto = Error;

/**
 * GET /api/v1/organization/{organization_id}/roadmap-nodes/
 */
export const GET_ORGANIZATION_ROADMAP_NODES_PATH = 'organization/{organization_id}/roadmap-nodes';
export type GetOrganizationRoadmapNodesRequestPathVariables = { organization_id: string };
export type GetOrganizationRoadmapNodesRequestQueryParams = {
  parent_id?: string | null;
} | null;
export type GetOrganizationRoadmapNodesSuccessDto = RoadmapNode[];
export type GetOrganizationRoadmapNodesErrorDto = Error;

const ORGANIZATION_SUMMARY_PATH = 'organization/{organization_id}/summary';
/**
 * GET /api/v1/organization/{organization_id}/summary
 */
export const GET_ORGANIZATION_SUMMARY_PATH = ORGANIZATION_SUMMARY_PATH;
export type GetOrganizationSummaryRequestPathVariables = { organization_id: string };
export type GetOrganizationSummaryRequestQueryParams = undefined;
export type GetOrganizationSummarySuccessDto = OrganizationSummary | Record<string, never>;
export type GetOrganizationSummaryErrorDto = Error;

/**
 * POST /api/v1/organization/{organization_id}/summary
 */
export const POST_ORGANIZATION_SUMMARY_PATH = ORGANIZATION_SUMMARY_PATH;
export type PostOrganizationSummaryRequestPathVariables = { organization_id: string };
export type PostOrganizationSummaryRequestQueryParams = undefined;
export type PostOrganizationSummaryRequestBodyData = Partial<OrganizationSummary>;
export type PostOrganizationSummarySuccessDto = OrganizationSummary;
export type PostOrganizationSummaryErrorDto = Error;
/**
 * PATCH /api/v1/organization/{organization_id}/summary
 */
export const PATCH_ORGANIZATION_SUMMARY_PATH = ORGANIZATION_SUMMARY_PATH;
export type PatchOrganizationSummaryRequestPathVariables = { organization_id: string };
export type PatchOrganizationSummaryRequestQueryParams = undefined;
export type PatchOrganizationSummaryRequestBodyData = Partial<OrganizationSummary>;
export type PatchOrganizationSummarySuccessDto = OrganizationSummary;
export type PatchOrganizationSummaryErrorDto = Error;
